import { useNavigate, useParams } from "react-router-dom"
import { FaRegEye } from "react-icons/fa"
import { Location, Organization, User, UserToLocation, UserToOrganization, ManagementCompany } from "../../../Interfaces"
import { AREA_MANAGER, ASSISTANT_MANAGER, MANAGER, roles, ADMIN, OWNER, COMPANY_MANAGER, SUPER_USER } from "../../../utils/roles"
import SaveButtonWithStatus from "../../../components/general/SaveButtonWithStatus"
import { isEmpty, phoneNumberAutoFormat, validatePasswords } from "../../../utils/helper"
import { useRef, useState, useMemo } from "react"
import { locationService } from "../../../services/locationService"
import { useQuery } from "@tanstack/react-query"
import useUser from "../../../hooks/useUser"
import CustomSelect from "../../../components/forms/CustomSelect"
import { userService } from "../../../services/userService"
import { organizationService } from "../../../services/organizationService"
import { useErrorBoundary } from "react-error-boundary"
import { MultiValue, SingleValue } from "react-select"
import { useQueryClient } from "@tanstack/react-query"
import ConfirmationModal from "../../../components/modals/ConfirmationModal"
import ImageDragDrop from "../../../components/forms/ImageDragDrop"
import { FaRegEyeSlash } from "react-icons/fa6"
import { managementCompanyService } from "../../../services/managementCompanyService"
import DynamicInput from "../../../components/forms/DynamicInput"
import Header from "../../../components/forms/Header"
import CompanyEmailToggle from "../../../components/forms/CompanyEmailToggle"

export default function UpdateProfile() {
  const { id, roleId: currentUserRoleId } = useUser()
  const { showBoundary } = useErrorBoundary()
  const { id: currentId } = useParams()
  const queryClient = useQueryClient()
  const { data: user } = useQuery(["user", currentId], () => currentId ? userService.getOne(Number(currentId)) : Promise.resolve(null), {
    onError: (error) => {
      console.error("Error fetching user data:", error)
      console.error("Current ID:", currentId)
    }
  })
  const thisUser = user // different from user in useUser hook. This is the user being updated
  console.log("thisUser:", thisUser)
  console.log("thisUser.roleId:", thisUser?.roleId)
  console.log("thisUser.roleName:", thisUser?.roleName)

  const formRef = useRef<HTMLFormElement>(null)
  if (isEmpty(thisUser)) {
    showBoundary("User not found")
    throw new Error("User not found")
  }

  const navigate = useNavigate()
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [saveStatus, setSaveStatus] = useState<string | null>(null)
  const [deleteStatus, setDeleteStatus] = useState<string | null>(null)
  const [userProfile, setUserProfile] = useState<User>(thisUser)
  const [updatePassword, setUpdatePassword] = useState<boolean>(false)
  const [newPassword, setNewPassword] = useState<string>("")
  const [newConfirmPassword, setNewConfirmPassword] = useState<string>("")
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<number[]>(thisUser?.userToOrganization?.map((org: UserToOrganization) => org.organizationId) || [])
  const [selectedLocationIds, setSelectedLocationsIds] = useState<number[]>(thisUser?.userToLocation?.map((loc: UserToLocation) => loc.locationId) || [])
  const [selectedManagementCompanyId, setSelectedManagementCompanyId] = useState<number>(thisUser?.managementCompanyId || 0)
  const [roleId, setRoleId] = useState<number>(thisUser.roleId || 0)
  console.log("Initial roleId state:", roleId)

  const { data: locationData } = useQuery(["locations"], () => locationService.getAllByUser(id))
  const { data: organizationData } = useQuery(["organizations"], () => organizationService.getAllByUser(id))
  const { data: managementCompany } = useQuery(["managementCompany", thisUser], () => managementCompanyService.getByUser(thisUser.id))
  const { data: managementCompanyList } = useQuery(["managementCompanies"], () => managementCompanyService.getAll(id), { enabled: true })
  const managementCompanyId = useRef(selectedManagementCompanyId || managementCompany?.id)
  // create a roles list to be used in the CustomSelect component. The rolesList should only allow roles that are less than the current user's role
  let filteredLocations = managementCompanyId.current ? locationData?.filter((location) => location?.managementCompanyId === managementCompanyId.current) : locationData
  let filteredOrganizations = managementCompanyId.current ? organizationData?.filter((org) => org?.managementCompanyId === managementCompanyId.current) : organizationData

  console.log("locationData:", locationData)
  console.log("filteredLocations:", filteredLocations)
  console.log("thisUser.userToLocation:", thisUser.userToLocation)
  console.log("managementCompanyId.current:", managementCompanyId.current)
  console.log("roleId:", roleId)
  console.log("currentUserRoleId:", currentUserRoleId)
  console.log("user.roleId:", user.roleId)
  console.log("Should show locations:", (thisUser.userToLocation && roleId === ASSISTANT_MANAGER) || (roleId === MANAGER && locationData && currentUserRoleId && user.roleId && currentUserRoleId > user.roleId) || (roleId === OWNER && locationData && currentUserRoleId && user.roleId && currentUserRoleId > user.roleId))

  const rolesList = Object.keys(roles)
    .filter((role) => currentUserRoleId && +role <= currentUserRoleId && +role < ADMIN)
    .map((role) => {
      return { value: role, label: roles[+role], name: roles[+role] }
    })
  console.log("rolesList:", rolesList)

  const initialRoleValue = useMemo(() => {
    if (roleId && roleId > 0) {
      const value = { value: roleId.toString(), label: roles[roleId], name: roles[roleId] }
      return value
    }
    // Default to ASSISTANT_MANAGER if no role is set
    const defaultValue = { value: ASSISTANT_MANAGER.toString(), label: roles[ASSISTANT_MANAGER], name: roles[ASSISTANT_MANAGER] }
    return defaultValue
  }, [roleId])

  const initialLocationsValue = useMemo(() => {
    if (selectedLocationIds.length > 0) {
      return selectedLocationIds.map(id => {
        const location = filteredLocations?.find(l => l && l.id === id)
        return {
          value: id ? id.toString() : '',
          label: location?.name || '',
          name: location?.name || ''
        }
      })
    }
    return []
  }, [selectedLocationIds, filteredLocations])

  const initialAreasValue = useMemo(() => {
    if (thisUser.userToOrganization && thisUser.userToOrganization.length > 0) {
      return thisUser.userToOrganization.map((org: UserToOrganization) => ({
        value: org.organizationId ? org.organizationId.toString() : '',
        label: org.organization?.name || '',
        name: org.organization?.name || ''
      }))
    }
    return []
  }, [thisUser.userToOrganization])

  const initialManagementCompanyValue = useMemo(() => {
    if (thisUser.managementCompanyId && managementCompanyList) {
      const company = managementCompanyList.find((company: ManagementCompany) => company.id === thisUser.managementCompanyId)
      if (company) {
        return { value: company.id, name: company.name }
      }
    }
    return undefined
  }, [thisUser.managementCompanyId, managementCompanyList])

  const handleUpdateProfile = async (e: { preventDefault: () => void }) => {
    e.preventDefault()

    console.log("handleUpdateProfile called for Super User:", thisUser.roleId === SUPER_USER)
    console.log("updatePassword state:", updatePassword)
    console.log("newPassword:", newPassword)
    console.log("newConfirmPassword:", newConfirmPassword)
    console.log("selectedManagementCompanyId:", selectedManagementCompanyId)
    console.log("managementCompanyId.current:", managementCompanyId.current)
    console.log("userProfile.pin:", userProfile.pin)
    console.log("Full userProfile:", userProfile)

    // Validate management company selection
    if (selectedManagementCompanyId === 0) {
      console.log("Management company validation failed - selectedManagementCompanyId is 0")
      alert("Please select a management company")
      return
    }

    // Validate PIN for Manager and Assistant Manager roles
    if ((roleId === MANAGER || roleId === ASSISTANT_MANAGER) && (!userProfile.pin || userProfile.pin.length < 4)) {
      alert("PIN is required and must be at least 4 digits for Manager and Assistant Manager roles")
      return
    }

    let userInfoToUpdate: Partial<User> = {
      id: userProfile.id,
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      phone: userProfile.phone,
      email: userProfile.email,
      roleId: roleId,
      managementCompanyId: managementCompanyId.current,
      pin: userProfile.pin,
      companyEmail: userProfile.companyEmail
    }

    // If the user being edited is a Super User, preserve their original role
    if (thisUser.roleId === SUPER_USER) {
      userInfoToUpdate.roleId = thisUser.roleId
    }

    console.log("Final userInfoToUpdate being sent to API:", userInfoToUpdate)

    if (updatePassword) {
      console.log("Validating passwords...")
      if (validatePasswords(newPassword, newConfirmPassword)) {
        console.log("Passwords validated successfully")
        userInfoToUpdate = { ...userInfoToUpdate, password: newPassword }
      } else {
        console.log("Password validation failed")
        return alert("Passwords do not match or are too short (minimum 6 characters)")
      }
    }
    setSaveStatus("Saving...")
    let updatedUser: Partial<User> | null = null
    try {
      updatedUser = await userService.update(userInfoToUpdate, selectedOrganizationIds, selectedLocationIds, imageFile)
    } catch (error) {
      showBoundary(error)
    }
    if (updatedUser) {
      setSaveStatus("Saved")
      setTimeout(() => {
        setSaveStatus(null)
        queryClient.invalidateQueries()
        queryClient.refetchQueries()
        navigate(`/app/users/${updatedUser?.id}`, { state: { user: { ...updatedUser } as User & Required<Pick<User, "userToLocation" | "userToOrganization" | "createdAt">> } })
      }, 1000)
    } else {
      setSaveStatus("Error")
      setTimeout(() => {
        setSaveStatus(null)
      }, 1500)
    }
  }

  const toggleDeleteModal = (e: { preventDefault: () => void }) => {
    e.preventDefault()
    setShowDeleteModal(!showDeleteModal)
  }

  const handleDeleteProfile = async () => {
    try {
      setDeleteStatus("Deleting...")
      const deletedUser = await userService.delete(thisUser.id)

      if (deletedUser) {
        setDeleteStatus("Deleted")
        setTimeout(() => {
          setDeleteStatus(null)
          toggleDeleteModal({ preventDefault: () => { } })
          queryClient.invalidateQueries(["users"])
          navigate("/app/")
        }, 1000)
      } else {
        setDeleteStatus("Error")
        setTimeout(() => {
          setDeleteStatus(null)
        }, 1500)
      }
    } catch (error) {
      showBoundary(error)
    }
  }

  const handleLocationSelection = (locations: MultiValue<any>) => {
    console.log("handleLocationSelection called with:", locations)
    const locationIds = locations.map((location): number => {
      if (typeof location === 'object' && location !== null) {
        if ('id' in location && typeof location.id === 'number') {
          return location.id
        }
        if ('value' in location) {
          return Number(location.value)
        }
      }
      return Number(location)
    })
    console.log("Setting locationIds to:", locationIds)
    setSelectedLocationsIds(locationIds)
    setSelectedOrganizationIds([])
  }

  const handleOrganizationSelect = (organizations: MultiValue<Organization>) => {
    const organizationIds = organizations.map((organization): number => organization.id!!)
    setSelectedOrganizationIds(organizationIds)
    setSelectedLocationsIds([])
  }

  const handleManagementCompanySelect = (managementCompany: SingleValue<ManagementCompany>) => {
    if (managementCompany) {
      setSelectedManagementCompanyId(managementCompany.id)
      managementCompanyId.current = managementCompany.id
    }
  }

  const handleRoleSelection = (role: { label: string; name: string; value: string }) => {
    const newRoleId = +role.value
    setRoleId(newRoleId)
    setUserProfile(prev => ({ ...prev, roleId: newRoleId }))

    // Clear locations and organizations if role is higher than AREA_MANAGER
    if (newRoleId > AREA_MANAGER) {
      setSelectedOrganizationIds([])
      setSelectedLocationsIds([])
    }
  }

  const handleChange = (e: { target: { validity: any; setCustomValidity(arg0: string): unknown; name: string; value: string } }) => {
    if (e.target.name === "password") {
      setNewPassword(e.target.value)
    } else if (e.target.name === "confirmPassword") {
      setNewConfirmPassword(e.target.value)
    } else if (e.target.name === "pin") {
      // Only allow digits for PIN, but store as string
      const digitsOnly = e.target.value.replace(/\D/g, '')
      setUserProfile({ ...userProfile, [e.target.name]: digitsOnly || undefined })
    } else {
      setUserProfile({ ...userProfile, [e.target.name]: e.target.value })
    }
  }

  // Log the conditions for rendering locations section
  console.log("Rendering locations section with:", {
    hasUserToLocation: !!thisUser.userToLocation,
    roleId,
    isManager: roleId === MANAGER,
    hasLocationData: !!locationData,
    currentUserRoleId,
    userRoleId: user.roleId,
    canEdit: currentUserRoleId && user.roleId ? currentUserRoleId > user.roleId : false
  })

  // Debug company email toggle visibility
  console.log('CompanyEmailToggle debug - roleId:', roleId, 'shouldShow:', roleId === 30)
  console.log('CompanyEmailToggle debug - thisUser:', thisUser)
  console.log('CompanyEmailToggle debug - thisUser.roleId:', thisUser?.roleId)
  console.log('CompanyEmailToggle debug - thisUser.companyEmail:', thisUser?.companyEmail)

  return (
    <div className="w-full min-h-full flex justify-center">
      <ConfirmationModal isOpen={showDeleteModal} title={"Delete Profile?"} message={"All data associated with this user will be deleted forever!"} onConfirm={handleDeleteProfile} onCancel={toggleDeleteModal} saveStatus={deleteStatus} />
      <section className="p-5 w-full h-full lg:max-w-4xl md:p-5 md:shadow-xl md:rounded-xl bg-white max-h-screen overflow-auto">
        <Header heading={`Update ${thisUser.firstName} ${thisUser.lastName}`} />
        <div className="lg:flex lg:justify-evenly items-center">
          <section className="lg:w-full">
            <form onSubmit={handleUpdateProfile} ref={formRef} className="mt-6 space-y-2">
              <div className="w-full">
                <table className="w-full">
                  <tbody className="space-y-4">
                    {/* Management Company Selection for All Users */}
                    {!managementCompanyList?.length ? (
                      <tr>
                        <td colSpan={2}>
                          <p className="text-red-500">Please add a management company first</p>
                        </td>
                      </tr>
                    ) : (
                      <tr className="flex items-center mb-4">
                        <td className="w-48 text-right pr-4 font-semibold">
                          Management Company:
                        </td>
                        <td className="flex-1">
                          <CustomSelect
                            list={managementCompanyList}
                            handleSelection={handleManagementCompanySelect}
                            placeholder="Select Management Company"
                            required={true}
                            className="rounded-lg"
                            styles={{ background: "#daf1ff" }}
                            defaultValues={initialManagementCompanyValue}
                          />
                        </td>
                      </tr>
                    )}

                    {/* Organization Selection for Area Managers */}
                    {roleId === AREA_MANAGER && (
                      <>
                        {!organizationData?.length ? (
                          <tr>
                            <td colSpan={2}>
                              <p className="text-red-500">Please add an area first</p>
                            </td>
                          </tr>
                        ) : (
                          <tr className="flex items-center mb-4">
                            <td className="w-48 text-right pr-4 font-semibold">
                              Areas:
                            </td>
                            <td className="flex-1">
                              <CustomSelect
                                list={filteredOrganizations as Organization[]}
                                placeholder="Select areas(s)"
                                isMulti
                                className="rounded-lg"
                                styles={{ background: "#daf1ff" }}
                                defaultValues={initialAreasValue}
                                handleSelection={handleOrganizationSelect}
                                required={true}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    )}

                    {/* Location Selection for Managers and Assistant Managers */}
                    {(roleId === MANAGER || roleId === ASSISTANT_MANAGER) && (
                      <>
                        {!locationData?.length ? (
                          <tr>
                            <td colSpan={2}>
                              <p className="text-red-500">Please add a location first</p>
                            </td>
                          </tr>
                        ) : (
                          <tr className="flex items-center mb-4">
                            <td className="w-48 text-right pr-4 font-semibold">
                              Locations:
                            </td>
                            <td className="flex-1">
                              <CustomSelect
                                list={filteredLocations?.filter((loc): loc is Location & { id: number } => !!loc && typeof loc.id === 'number').map(loc => ({
                                  value: loc.id ? loc.id.toString() : '',
                                  label: loc.name,
                                  name: loc.name
                                })) || []}
                                placeholder="Select location(s)"
                                isMulti
                                className="rounded-lg"
                                styles={{ background: "#daf1ff" }}
                                defaultValues={initialLocationsValue}
                                handleSelection={handleLocationSelection}
                                required={true}
                              />
                            </td>
                          </tr>
                        )}
                      </>
                    )}

                    <tr className="flex items-center mb-4">
                      <td className="w-48 text-right pr-4 font-semibold">
                        First Name:
                      </td>
                      <td className="flex-1">
                        <DynamicInput
                          className="w-full rounded-md bg-gray-50 border-gray-300 text-black px-3 py-2"
                          type="text"
                          value={userProfile.firstName || ""}
                          name="firstName"
                          onChange={handleChange}
                        />
                      </td>
                    </tr>

                    <tr className="flex items-center mb-4">
                      <td className="w-48 text-right pr-4 font-semibold">
                        Last Name:
                      </td>
                      <td className="flex-1">
                        <DynamicInput
                          className="w-full rounded-md bg-gray-50 border-gray-300 text-black px-3 py-2"
                          type="text"
                          value={userProfile.lastName || ""}
                          name="lastName"
                          onChange={handleChange}
                        />
                      </td>
                    </tr>

                    <tr className="flex items-center mb-4">
                      <td className="w-48 text-right pr-4 font-semibold">
                        Phone:
                      </td>
                      <td className="flex-1">
                        <DynamicInput
                          className="w-full rounded-md bg-gray-50 border-gray-300 text-black px-3 py-2"
                          type="tel"
                          value={phoneNumberAutoFormat(userProfile.phone || "")}
                          name="phone"
                          onChange={handleChange}
                          pattern={"^\\d{3}-\\d{3}-\\d{4}$"}
                          maxLength={12}
                          isRequired={true}
                        />
                      </td>
                    </tr>

                    <tr className="flex items-center mb-4">
                      <td className="w-48 text-right pr-4 font-semibold">
                        Email:
                      </td>
                      <td className="flex-1">
                        <DynamicInput
                          className="w-full rounded-md bg-gray-50 border-gray-300 text-black px-3 py-2"
                          type="email"
                          value={userProfile.email || ""}
                          name="email"
                          isRequired={true}
                          onChange={handleChange}
                        />
                      </td>
                    </tr>

                    {/* PIN field for Manager and Assistant Manager (required), optional for higher roles */}
                    {(roleId === MANAGER || roleId === ASSISTANT_MANAGER || roleId === AREA_MANAGER || roleId === COMPANY_MANAGER || roleId === OWNER || roleId === ADMIN) && (
                      <tr className="flex items-center mb-4">
                        <td className="w-48 text-right pr-4 font-semibold">
                          PIN:
                        </td>
                        <td className="flex-1">
                          <DynamicInput
                            className="w-full rounded-md bg-gray-50 border-gray-300 text-black px-3 py-2"
                            type="text"
                            value={userProfile.pin || ""}
                            name="pin"
                            isRequired={roleId === MANAGER || roleId === ASSISTANT_MANAGER}
                            onChange={handleChange}
                            minLength={4}
                            maxLength={8}
                          />
                        </td>
                      </tr>
                    )}

                    <tr className="flex items-center mb-4">
                      <td className="w-48 text-right pr-4 font-semibold">
                        Role:
                      </td>
                      <td className="flex-1">
                        <CustomSelect
                          list={rolesList}
                          className="rounded-lg"
                          styles={{ background: "#daf1ff" }}
                          defaultValues={initialRoleValue}
                          handleSelection={handleRoleSelection}
                          required={true}
                          disabled={thisUser.roleId === SUPER_USER}
                        />
                      </td>
                    </tr>

                    {/* Company Email Toggle - only visible for role 30 */}
                    <tr className="flex items-center mb-4">
                      <td className="w-48 text-right pr-4 font-semibold">
                        Company Email:
                      </td>
                      <td className="flex-1">
                        <CompanyEmailToggle
                          userId={thisUser.id}
                          userRole={roleId}
                          initialValue={thisUser.companyEmail || false}
                          onToggle={(enabled) => {
                            console.log('Company email setting changed to:', enabled);
                            setUserProfile({ ...userProfile, companyEmail: enabled });
                          }}
                        />
                      </td>
                    </tr>

                    {/* Password fields inside the form */}
                    {updatePassword && (
                      <>
                        <tr className="flex items-center mb-4">
                          <td className="w-48 text-right pr-4 font-semibold">
                            New Password:
                          </td>
                          <td className="flex-1 flex items-center">
                            <DynamicInput
                              className="flex-1 rounded-md bg-gray-50 border-gray-300 text-black px-3 py-2 mr-2"
                              type={showPassword ? "text" : "password"}
                              value={newPassword || ""}
                              name="password"
                              minLength={6}
                              maxLength={50}
                              onChange={handleChange}
                            />
                            {showPassword ?
                              <FaRegEye onClick={() => setShowPassword(false)} className="cursor-pointer" /> :
                              <FaRegEyeSlash onClick={() => setShowPassword(true)} className="cursor-pointer" />
                            }
                          </td>
                        </tr>
                        <tr className="flex items-center mb-4">
                          <td className="w-48 text-right pr-4 font-semibold">
                            Confirm Password:
                          </td>
                          <td className="flex-1">
                            <DynamicInput
                              className="w-full rounded-md bg-gray-50 border-gray-300 text-black px-3 py-2"
                              type={showPassword ? "text" : "password"}
                              value={newConfirmPassword || ""}
                              name="confirmPassword"
                              minLength={6}
                              onChange={handleChange}
                            />
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </form>
          </section>
          <section className="lg:w-full lg:pl-5 lg:m-auto">
            <ImageDragDrop setImageState={setImageFile} title="Upload Photo" />
          </section>
        </div>
        <div className="flex justify-between items-center mt-4">
          <button
            className="bg-primary text-white p-2 rounded-lg hover:shadow-right-lg transform active:translate-y-1 active:scale-[.99] active:shadow-none font-medium"
            onClick={(e) => {
              e.preventDefault()
              setUpdatePassword(!updatePassword)
            }}
          >
            {updatePassword ? "Cancel Update" : "Update Password"}
          </button>
          {currentUserRoleId && user.roleId && currentUserRoleId > user.roleId && (
            <button className="bg-red-500 text-white p-2 rounded-lg hover:bg-red-600 transform active:translate-y-1 active:scale-[.99] active:shadow-none font-medium" onClick={toggleDeleteModal}>
              Delete User
            </button>
          )}
        </div>
        <SaveButtonWithStatus saveStatus={saveStatus} handleSave={handleUpdateProfile} isForm={true} className="w-full mt-4" />
      </section>
    </div>
  )
}
